import styles from "./ColorBlock.module.css";

/**
 * ColorBlock — a rounded, softly-lit gradient box.
 *
 * Originally extracted from the landing page's feature cards, where it sits
 * above a title + description as a placeholder "image". Use a named `color`
 * for the built-in palette, or pass a raw CSS `gradient` (or any `style`) to
 * override — handy when a real image/illustration replaces the placeholder
 * but you want to keep the same frame.
 *
 * @param {("blue"|"orange"|"purple"|"green"|"lime"|"teal")} [color="blue"]
 * @param {string} [gradient] - Raw CSS background to override the named color.
 * @param {React.ReactNode} [children] - Optional content rendered inside.
 */
const colorClass = {
  blue: styles.colorBlue,
  orange: styles.colorOrange,
  purple: styles.colorPurple,
  green: styles.colorGreen,
  lime: styles.colorLime,
  teal: styles.colorTeal,
};

export default function ColorBlock({
  color = "blue",
  gradient,
  bordered = true,
  className = "",
  style,
  children,
  ...rest
}) {
  // `color={null}` (or any falsy) → no gradient, e.g. when a real image fills
  // the block and a background would only bleed at the rounded corners.
  const resolvedColor = gradient || !color ? "" : colorClass[color] ?? colorClass.blue;
  const classes = [styles.block, !bordered && styles.borderless, resolvedColor, className]
    .filter(Boolean)
    .join(" ");
  const resolvedStyle = gradient ? { background: gradient, ...style } : style;

  return (
    <div className={classes} style={resolvedStyle} {...rest}>
      {children}
    </div>
  );
}
